import { useEffect, useState } from 'react';
import { GitBranch } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface ConversionRates {
  leadToOpportunity: number;
  opportunityToWon: number;
  leadsConsidered: number;
  opportunitiesClosed: number;
}

/**
 * Story 56.2 — funnel conversion. Lead → Opp → Won, with the rates
 * (which is what matters more than raw counts per Alfonso's framing).
 */
export function FunnelConversionWidget() {
  const [data, setData] = useState<ConversionRates | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<ConversionRates>('/analytics/conversion')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading conversion…
      </div>
    );
  }
  if (!data) return null;

  const l2o = Number(data.leadToOpportunity) * 100;
  const o2w = Number(data.opportunityToWon) * 100;
  const oppCount = Math.round(data.leadsConsidered * Number(data.leadToOpportunity));
  const wonCount = Math.round(oppCount * Number(data.opportunityToWon));

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Funnel conversion
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stage label="Leads" count={data.leadsConsidered} />
        <Stage label="Opportunities" count={oppCount} pct={l2o} />
        <Stage label="Won" count={wonCount} pct={o2w} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3 text-xs text-muted-foreground">
        <div>
          Lead → Opp:{' '}
          <span className="font-semibold text-foreground">{l2o.toFixed(1)}%</span>
        </div>
        <div>
          Opp → Won:{' '}
          <span className="font-semibold text-foreground">{o2w.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}

function Stage({ label, count, pct }: { label: string; count: number; pct?: number }) {
  return (
    <div className="rounded-md bg-muted/40 p-3 text-center">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{count.toLocaleString()}</div>
      {pct !== undefined && (
        <div className="text-xs text-muted-foreground">{pct.toFixed(1)}% from prev</div>
      )}
    </div>
  );
}
